import React, { useEffect, useState } from 'react';
import {
  Animated,
  Pressable,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface SwitchProps extends PressableAnalyticsProps {
  /** Shows the current status of the switch */
  isOn: boolean;
  /** it's a function that gets called when we toggle the switch, it also give a switch value. e.g  const onSwitchPress = (value: boolean) => {} */
  onValueChange: (boolean) => void;
  /** Used to disable the user interaction */
  showStatusLabel?: boolean;
  /** Shows the current status of toggle (On/Off) */
  isDisabled?: boolean;
  /** Shows loading status */
  isLoading?: boolean;
}

interface ComposableStyle {
  buttonStyle: ViewStyle;
  head: ViewStyle;
  checkmark: TextStyle;
}

interface ComposableStyles {
  default: ComposableStyle;
  disable: ComposableStyle;
  focus: ComposableStyle;
  hover: ComposableStyle;
  press: ComposableStyle;
}

export const Switch = ({
  isOn,
  onValueChange,
  showStatusLabel = false,
  isDisabled = false,
  analyticsId = '',
  customAttributes = null,
  isLoading = false,
}: SwitchProps) => {
  const switchAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes: { ...customAttributes, toggled: String(!isOn) },
  });
  const [animatedValue] = useState(new Animated.Value(isOn ? 1 : 0));
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const computedStyles = composeButtonStyles(
    isOn,
    isHovered,
    isPressed,
    isFocused,
    isDisabled,
  );

  useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: isOn ? 1 : 0,
      duration: 200,
      useNativeDriver: true,
    }).start();
  }, [animatedValue, isOn]);

  const translateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [4, 22],
  });

  //TODO: This should be moved to the localized string file. Amol N. - May 24, 2024
  const statusTitle = isOn ? 'On' : 'Off';

  return (
    <View style={baseStyles.container}>
      {showStatusLabel ? (
        <BodySmBoldText style={baseStyles.statusTextStyle}>
          {statusTitle}
        </BodySmBoldText>
      ) : null}
      {!isLoading ? (
        <Pressable
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onPressIn={() => setIsPressed(true)}
          onPressOut={() => setIsPressed(false)}
          onPress={() => {
            switchAnalytics();
            onValueChange(!isOn);
          }}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          style={[baseStyles.pressable, computedStyles.buttonStyle]}
          role="switch"
          testID={`switch-${statusTitle}`}
        >
          <View style={baseStyles.innerContainer}>
            <Animated.View
              style={{
                transform: [{ translateX }],
              }}
            >
              <View style={baseStyles.innerView}>
                <View style={[baseStyles.head, computedStyles.head]} />
                {isOn ? (
                  <View style={baseStyles.checkmark}>
                    <Svg
                      width={20}
                      height={20}
                      viewBox="0 0 20 20"
                      color={computedStyles.checkmark.color}
                    >
                      <Path
                        d="M5 9.5L8.5 13L14.5 7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Svg>
                  </View>
                ) : null}
              </View>
            </Animated.View>
          </View>
        </Pressable>
      ) : (
        <View style={baseStyles.loadingView} testID="loader">
          <LoadingSpinner />
        </View>
      )}
    </View>
  );
};

const baseStyles = StyleSheet.create({
  checkmark: {
    position: 'absolute',
  },
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingVertical: theme.spacing.xs,
  },
  head: {
    borderRadius: 100,
    height: 20,
    width: 20,
  },
  innerContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flex: 1,
    position: 'relative',
  },
  innerView: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingView: {
    alignItems: 'center',
  },
  pressable: {
    borderRadius: 28,
    borderWidth: 1,
    height: 28,
    width: 48,
  },
  statusTextStyle: {
    color: theme.color.text.primary,
    display: 'flex',
    paddingRight: theme.spacing.s,
    paddingVertical: theme.spacing.xs,
  },
});

const switchOffStyle: ComposableStyles = {
  default: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.background.primary,
      borderColor: theme.color.interactive.primary,
    },
    checkmark: {
      color: theme.color.interactive.primary,
      display: 'none',
    },
    head: {
      backgroundColor: theme.color.interactive.primary,
    },
  }),
  disable: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.background.primary,
      borderColor: theme.color.interactive.disabled.border,
    },
    checkmark: {
      color: theme.color.interactive.inverse,
      display: 'none',
    },
    head: {
      backgroundColor: theme.color.interactive.disabled.background,
    },
  }),
  focus: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.interactive.inverse,
      borderColor: theme.color.border.focused,
    },
    checkmark: {
      color: theme.color.interactive.inverse,
      display: 'none',
    },
    head: {
      backgroundColor: theme.color.interactive.primary,
    },
  }),
  hover: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.interactive.inverse,
      borderColor: theme.color.interactive.primaryHovered,
    },
    checkmark: {
      color: theme.color.interactive.primaryHovered,
      display: 'none',
    },
    head: {
      backgroundColor: theme.color.interactive.primaryHovered,
    },
  }),
  press: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.interactive.inverse,
      borderColor: theme.color.interactive.primaryPressed,
    },
    checkmark: {
      color: theme.color.interactive.inverse,
      display: 'none',
    },
    head: {
      backgroundColor: theme.color.interactive.primaryPressed,
    },
  }),
};

const switchOnStyle: ComposableStyles = {
  default: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.interactive.primary,
      borderColor: theme.color.interactive.primary,
    },
    checkmark: {
      color: theme.color.interactive.primary,
      display: 'flex',
    },
    head: {
      backgroundColor: theme.color.background.primary,
    },
  }),
  disable: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.interactive.disabled.background,
      borderColor: 'transparent',
    },
    checkmark: {
      color: theme.color.interactive.disabled.background,
      display: 'flex',
    },
    head: {
      backgroundColor: theme.color.interactive.disabled.text,
    },
  }),
  focus: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.interactive.primary,
      borderColor: theme.color.border.focused,
    },
    checkmark: {
      color: theme.color.interactive.primary,
      display: 'flex',
    },
    head: {
      backgroundColor: theme.color.interactive.inverse,
    },
  }),
  hover: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.interactive.primaryHovered,
      borderColor: theme.color.interactive.primaryHovered,
    },
    checkmark: {
      color: theme.color.interactive.primaryHovered,
      display: 'flex',
    },
    head: {
      backgroundColor: theme.color.interactive.inverse,
    },
  }),
  /* eslint-disable react-native/no-unused-styles -- dynamic style keys require disabling the rule */
  press: StyleSheet.create({
    buttonStyle: {
      backgroundColor: theme.color.interactive.primaryPressed,
      borderColor: theme.color.interactive.primaryPressed,
    },

    checkmark: {
      color: theme.color.interactive.primaryPressed,
      display: 'flex',
    },

    head: {
      backgroundColor: theme.color.interactive.inverse,
    },
  }),
  /* eslint-enable react-native/no-unused-styles */
};

const composeButtonStyles = (
  isOn: SwitchProps['isOn'],
  isHovered,
  isPressed,
  isFocused,
  isDisabled,
): ComposableStyle => {
  if (isOn) {
    if (isDisabled) {
      return {
        buttonStyle: switchOnStyle.disable.buttonStyle,
        head: switchOnStyle.disable.head,
        checkmark: switchOnStyle.disable.checkmark,
      };
    } else if (isHovered) {
      return {
        buttonStyle: switchOnStyle.hover.buttonStyle,
        head: switchOnStyle.hover.head,
        checkmark: switchOnStyle.hover.checkmark,
      };
    } else if (isPressed) {
      return {
        buttonStyle: switchOnStyle.press.buttonStyle,
        head: switchOnStyle.press.head,
        checkmark: switchOnStyle.press.checkmark,
      };
    } else if (isFocused) {
      return {
        buttonStyle: switchOnStyle.focus.buttonStyle,
        head: switchOnStyle.focus.head,
        checkmark: switchOnStyle.focus.checkmark,
      };
    }

    return {
      buttonStyle: switchOnStyle.default.buttonStyle,
      head: switchOnStyle.default.head,
      checkmark: switchOnStyle.default.checkmark,
    };
  }

  if (isDisabled) {
    return {
      buttonStyle: switchOffStyle.disable.buttonStyle,
      head: switchOffStyle.disable.head,
      checkmark: switchOffStyle.disable.checkmark,
    };
  } else if (isHovered) {
    return {
      buttonStyle: switchOffStyle.hover.buttonStyle,
      head: switchOffStyle.hover.head,
      checkmark: switchOffStyle.hover.checkmark,
    };
  } else if (isPressed) {
    return {
      buttonStyle: switchOffStyle.press.buttonStyle,
      head: switchOffStyle.press.head,
      checkmark: switchOffStyle.press.checkmark,
    };
  } else if (isFocused) {
    return {
      buttonStyle: switchOffStyle.focus.buttonStyle,
      head: switchOffStyle.focus.head,
      checkmark: switchOffStyle.focus.checkmark,
    };
  }

  return {
    buttonStyle: switchOffStyle.default.buttonStyle,
    head: switchOffStyle.default.head,
    checkmark: switchOffStyle.default.checkmark,
  };
};

export default Switch;
